import { useState, useCallback, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';
import { COUNTRIES } from '../../constants/countries';

const BASE = '/pricing-rules/insurance';

const DEFAULT_CONFIG = {
  marginType: 'Percentage',
  marginValue: 12,
  applyOn: 'total',
  active: true,
  startDate: '2025-01-01',
  endDate: '2025-12-31',
};

/**
 * Map API margin_applied_on to frontend applyOn
 */
const APPLIED_ON_FROM_API = {
  FLIGHT_ONLY: 'flight',
  HOTEL_ONLY: 'hotel',
  FLIGHT_HOTEL_TRANSFER: 'total',
};

const APPLIED_ON_TO_API = {
  flight: 'FLIGHT_ONLY',
  hotel: 'HOTEL_ONLY',
  total: 'FLIGHT_HOTEL_TRANSFER',
};

/**
 * Map API margin_type to frontend marginType
 */
const MARGIN_TYPE_FROM_API = {
  PERCENTAGE: 'Percentage',
  FIXED_AMOUNT: 'Fixed',
};

const MARGIN_TYPE_TO_API = {
  Percentage: 'PERCENTAGE',
  Fixed: 'FIXED_AMOUNT',
};

/**
 * Map API rule to frontend config format
 */
function mapRuleToConfig(rule) {
  if (!rule) return null;
  const marginValueStr = rule.margin_value;
  const marginType = MARGIN_TYPE_FROM_API[rule.margin_type] || 'Percentage';
  const rawValue = marginType === 'Percentage'
    ? parseFloat(String(marginValueStr).replace('%', '')) || 0
    : parseFloat(String(marginValueStr).replace(/[€,]/g, '')) || 0;

  console.log('Mapping rule to config, rule.country_exclusions:', rule.country_exclusions);
  console.log('Type of rule.country_exclusions:', typeof rule.country_exclusions);
  console.log('Length of rule.country_exclusions:', rule.country_exclusions?.length);

  const config = {
    id: rule.id,
    insurance_name: rule.insurance_name,
    insurance_description: rule.insurance_description,
    marginType,
    marginValue: rawValue,
    applyOn: APPLIED_ON_FROM_API[rule.margin_applied_on] || 'total',
    active: rule.status ?? true,
    startDate: rule.start_date || '',
    endDate: rule.end_date || '',
    country_exclusions: rule.country_exclusions || [],
  };

  console.log('Final config object being returned:', config);
  console.log('Final config.country_exclusions:', config.country_exclusions);

  return config;
}

/**
 * Map frontend config to API payload (for CREATE)
 */
function mapConfigToApi(form) {
  // Process margin value based on type
  let processedMarginValue;
  if (form.marginType === 'Fixed') {
    // For fixed amount, remove any currency symbols and ensure it's a number
    processedMarginValue = parseFloat(String(form.marginValue).replace(/[€,%]/g, '')) || 0;
  } else {
    // For percentage, remove % and convert to number
    processedMarginValue = parseFloat(String(form.marginValue).replace('%', '')) || 0;
  }

  // Map excluded countries array to country_exclusions format
  const country_exclusions = (form.excludedCountries || []).map(code => {
    const country = COUNTRIES.find(c => c.code === code);
    return {
      country_code: code,
      country_name: country ? country.name : code
    };
  });

  return {
    insurance_name: form.insuranceName || 'Standard Travel Protection',
    insurance_description: form.marketingText || 'Comprehensive travel insurance covering cancellations, medical emergencies, and trip delays.',
    margin_type: MARGIN_TYPE_TO_API[form.marginType] || 'PERCENTAGE',
    margin_value: processedMarginValue,
    margin_applied_on: APPLIED_ON_TO_API[form.applyOn] || 'FLIGHT_HOTEL_TRANSFER',
    country_exclusions,
    start_date: form.startDate || form.start_date,
    end_date: form.endDate || form.end_date || null,
    status: form.active ?? true,
  };
}

/**
 * GET Insurance Rules (list)
 */
export function useInsuranceRules(params = {}) {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasInitializedRef = useRef(false);

  const fetchList = useCallback(async (queryParams = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(BASE, {
        params: { ...params, ...queryParams },
      });
      const list = res?.data ?? [];
      setData(list);
      setSummary(res?.summary ?? null);
      return list;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load insurance rules';
      setError(msg);
      setData([]);
      setSummary(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchList();
    }
  }, [fetchList]);

  return { data, summary, loading, error, refetch: fetchList };
}

/**
 * GET Current Insurance Status (active rule summary)
 */
export function useInsuranceStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/status`);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load insurance status';
      setError(msg);
      setData(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { data, loading, error, refetch: fetchStatus };
}

/**
 * Combined: Get active config for UI (from rules list)
 * Uses active rule or returns defaults when none
 */
export function useInsuranceConfig() {
  const { data: rules, loading, error, refetch } = useInsuranceRules();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (loading) return;
    console.log('All rules from API:', rules);
    
    // Always use the most recent rule (whether active or not) for editing
    const mostRecentRule = rules?.length > 0 ? rules[rules.length - 1] : null;
    console.log('Most recent rule:', mostRecentRule);
    
    if (mostRecentRule) {
      console.log('Using most recent rule, mapping to config...');
      setConfig(mapRuleToConfig(mostRecentRule));
    } else {
      console.log('No rules found, using default config');
      setConfig(DEFAULT_CONFIG);
    }
  }, [rules, loading]);

  console.log('Final config from useInsuranceConfig before return:', config);
  const mostRecentRule = rules?.length > 0 ? rules[rules.length - 1] : null;
  return {
    data: config,
    loading,
    error,
    refetch,
    activeRuleId: mostRecentRule?.id ?? null,
    hasActiveRule: !!mostRecentRule,
  };
}

/**
 * CREATE Insurance Rule
 */
export function useCreateInsuranceRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (form) => {
    setLoading(true);
    setError(null);
    try {
      const payload = mapConfigToApi(form);
      await axiosInstance.post(BASE, payload);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to create insurance rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * UPDATE Insurance Rule
 */
export function useUpdateInsuranceRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, form) => {
    if (!id) throw new Error('Insurance rule id is required');
    setLoading(true);
    setError(null);
    try {
      // Process margin value based on type
      let processedMarginValue;
      if (form.marginType === 'Fixed') {
        // For fixed amount, remove any currency symbols and ensure it's a number
        processedMarginValue = parseFloat(String(form.marginValue).replace(/[€,%]/g, '')) || 0;
      } else {
        // For percentage, remove % and convert to number
        processedMarginValue = parseFloat(String(form.marginValue).replace('%', '')) || 0;
      }

      const payload = {
        ...(form.insuranceName !== undefined && { insurance_name: form.insuranceName }),
        ...(form.marketingText !== undefined && { insurance_description: form.marketingText }),
        margin_type: MARGIN_TYPE_TO_API[form.marginType] || 'PERCENTAGE',
        margin_value: processedMarginValue,
        margin_applied_on: APPLIED_ON_TO_API[form.applyOn] || 'FLIGHT_HOTEL_TRANSFER',
        start_date: form.startDate || form.startDate,
        end_date: form.endDate || form.endDate || null,
        status: form.active ?? true,
      };

      // Handle country exclusions - send complete list to replace existing
      if (form.excludedCountries && form.excludedCountries.length > 0) {
        const country_exclusions = form.excludedCountries.map(code => {
          const country = COUNTRIES.find(c => c.code === code);
          return {
            country_code: code,
            country_name: country ? country.name : code
          };
        });
        payload.country_exclusions = country_exclusions;
      } else {
        // Send empty array to clear all exclusions
        payload.country_exclusions = [];
      }
      
      console.log('Final payload being sent:', payload);
      await axiosInstance.put(`${BASE}/${id}`, payload);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to update insurance rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * GET Single Insurance Rule by ID
 */
export function useGetInsuranceRule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('Insurance rule id is required');
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/${id}`);
      const rule = res?.data ?? res;
      const mapped = mapRuleToConfig(rule);
      setData(mapped);
      return mapped;
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to load insurance rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, data, loading, error };
}

/**
 * DELETE Insurance Rule
 */
export function useDeleteInsuranceRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('Insurance rule id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to delete insurance rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * GET Insurance Rules Count
 */
export function useInsuranceRulesCount() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/count`);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load insurance count';
      setError(msg);
      setData(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { data, loading, error, refetch: fetchCount };
}
